import { FormControl, FormControlLabel, Radio, RadioGroup, Typography, useTheme } from "@mui/material";
import { NegativeFeedbackCategory } from "../../../api/types/feedbackTypes";
import { useTranslation } from "react-i18next";

type NegativeFeedbackCategoriesFormProps = {
    selectedOption: string;
    setSelectedOption: (newValue: string) => void;
};

/**
 * NegativeFeedbackCategoryForm allows users to select a predefined category
 * when submitting negative feedback. It renders a group of radio buttons
 * with labels based on the `NegativeFeedbackCategories` enum.
 *
 * Props:
 * - `selectedOption` (string) - The currently selected feedback category key.
 * - `setSelectedOption` (function) - Callback to update the selected category key.
 *
 * @returns {JSX.Element} A group of styled radio buttons representing negative feedback categories.
 *
 * @example
 * <NegativeFeedbackCategoryForm
 *   selectedOption={selectedOption}
 *   setSelectedOption={setSelectedOption}
 * />
 */
function NegativeFeedbackCategoryForm({ selectedOption, setSelectedOption }: NegativeFeedbackCategoriesFormProps) {
    const theme = useTheme();
    const { t } = useTranslation("translation", { keyPrefix: "chat" });

    const categories = [
        { key: "noAnswer", label: t("feedback.categories.noAnswer") },
        { key: "incompleteAnswer", label: t("feedback.categories.incompleteAnswer") },
        { key: "irrelevantAnswer", label: t("feedback.categories.irrelevantAnswer") },
        { key: "missingSource", label: t("feedback.categories.missingSource") },
        { key: "incorrectAnswer", label: t("feedback.categories.incorrectAnswer") },
        { key: "other", label: t("feedback.categories.other") }
    ];

    return (
        <FormControl component="fieldset" sx={{ marginBottom: 2 }}>
            <RadioGroup value={selectedOption} onChange={e => setSelectedOption(e.target.value)} sx={{ gap: "0.3rem" }}>
                {categories.map(category => (
                    <FormControlLabel
                        key={category.key}
                        value={category.key}
                        control={
                            <Radio
                                sx={{
                                    color: theme.palette.text.primary,
                                    "&.Mui-checked": {
                                        color: theme.palette.primary.light
                                    }
                                }}
                            />
                        }
                        label={<Typography sx={{ fontSize: "1rem" }}>{category.label}</Typography>}
                    />
                ))}
            </RadioGroup>
        </FormControl>
    );
}

export default NegativeFeedbackCategoryForm;
